import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Modal, { MODAL_SIZE } from "components/Modal";
import { FileDropzone } from "components/Form";
import { useInterviewQuestionAIVideoContext, useToast } from "hooks";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";

/**
 * InterviewQuestionVideoActionModal
 *
 * @param {Function} onUploadFromDevice - Callback for handling video upload from device
 *
 * This modal provides options to either generate an AI video or upload a video from the user's device for a specific interview question. It displays the status of AI video generation and allows users to select a video file for upload.
 *
 * @param {Function}  onUploadFromDevice - Callback function to handle video upload from device. Receives an object with questionId and file properties.
 * @param {Boolean}   uploadingQuestionVideo - Indicates if a video upload is currently in progress, used to disable interactions and show loading state.
 */
const InterviewQuestionVideoActionModal = ({ onUploadFromDevice, uploadingQuestionVideo, ...props }) => {
  const { toast } = useToast();
  const {
    showVideoActionModal: show,
    videoActionQuestion: question,
    closeVideoActionModal: onClose,
    openAiVideoModal,
    loading,
  } = useInterviewQuestionAIVideoContext();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [ignoreExistingTaskOnGenerate, setIgnoreExistingTaskOnGenerate] = useState(false);

  const resetUploadState = () => {
    setSelectedFiles([]);
    setIgnoreExistingTaskOnGenerate(false);
  };

  useEffect(() => {
    if (!show) {
      resetUploadState();
    } else {
      setIgnoreExistingTaskOnGenerate(Boolean(question?.ignoreExistingTaskOnGenerate));
    }
  }, [show, question]);

  const handleUploadClick = async () => {
    const file = selectedFiles?.[0];
    if (!file || uploadingQuestionVideo) return;

    try {
      await onUploadFromDevice({ questionId: question?.id, file });
      resetUploadState();
      onClose();
    } catch {
      toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.VideoActionModal.UploadError" />);
    }
  };

  const handleFileChange = (_name, files, errors = []) => {
    if (errors.length > 0) {
      errors.forEach(({ message }) => toast.error(message));
      return;
    }

    setSelectedFiles(files || []);
  };

  const isBusy = loading || uploadingQuestionVideo;

  return (
    <Modal
      show={show}
      onClose={isBusy ? undefined : onClose}
      onSubmit={handleUploadClick}
      title={
        <FormattedMessage
          id="Missions.Roles.RoleVetting.Interviews.VideoActionModal.Title"
          values={{ name: question?.title }}
        />
      }
      size={MODAL_SIZE.large}
      showSave={true}
      showCancel
      canSubmit={Boolean(selectedFiles.length)}
      primaryButtonMessageId="Missions.Roles.RoleVetting.Interviews.VideoActionModal.Upload"
      secondaryButtonMessageId="Global.Close"
      loading={isBusy}
      {...props}
    >
      <div css={styles.body}>
        <div css={styles.column}>
          <button
            type="button"
            css={styles.generatePanel}
            onClick={() => openAiVideoModal(question, { ignoreExistingTask: ignoreExistingTaskOnGenerate })}
            disabled={isBusy}
          >
            <div css={styles.generateTitle}>
              <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.VideoActionModal.Generate" />
            </div>
            <div css={styles.generateDescription}>
              <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.VideoActionModal.GenerateDescription" />
            </div>
          </button>
        </div>
        <div css={styles.column}>
          <FileDropzone
            name="question-video-upload"
            value={selectedFiles}
            onChange={handleFileChange}
            accept="video/*"
            maxFiles={1}
            maxSize={200 * 1024 * 1024}
            dropzoneTextId="Missions.Roles.RoleVetting.Interviews.VideoActionModal.Dropzone"
            disabled={isBusy}
          />
        </div>
      </div>
    </Modal>
  );
};

const styles = {
  body: css`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 2rem;
    padding: 1rem 0;
  `,
  column: css`
    min-width: 0;
    display: flex;

    .ai-video-upload-dropzone {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .ai-video-upload-dropzone > div:first-of-type {
      flex: 1;
      min-height: 17.5rem;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  `,
  generatePanel: css`
    width: 100%;
    height: 100%;
    border: 2px dashed ${colors.grayAnatomyLight1};
    border-radius: 0.8rem;
    background: ${colors.white};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.6rem;
    padding: 2.4rem;
    text-align: center;
    cursor: pointer;

    &:hover {
      border-color: ${colors.purpleRainBase};
      background: ${colors.grayAnatomyLight5};
    }
  `,
  generateTitle: css`
    font-size: ${fontSize.medium};
    font-weight: 600;
    color: ${colors.grayAnatomyBase};
  `,
  generateDescription: css`
    font-size: ${fontSize.small};
    color: ${colors.grayAnatomyLight1};
    line-height: 1.6;
  `,
};

InterviewQuestionVideoActionModal.propTypes = {
  onUploadFromDevice: PropTypes.func.isRequired,
  uploadingQuestionVideo: PropTypes.bool,
};

export default InterviewQuestionVideoActionModal;
